import { Injectable } from '@angular/core';
import { UserSelectionData } from 'appRootPath/models/commercial-papers.model';
import { BehaviorSubject } from 'rxjs';
import { setHolidays } from '../utils';
import * as CryptoJS from 'crypto-js';
import { CpMarketplaceApiService } from './cp-marketplace-api.service';


@Injectable()
export class SharedDataService {
  private loginSubject = new BehaviorSubject<Boolean>(false);
  private loaderSubject = new BehaviorSubject<Boolean>(false);
  private corporateSignUpData = null;
  private encryptionKey : any;

  public userData;
  ISINList: any = [];
  cpList: any = [];
  isExistingISINFlag: boolean = false;
  loginObservable = this.loginSubject.asObservable();
  loaderObservable = this.loaderSubject.asObservable();
  loadersCount = 0;

  // userSelectionSubject helps us in getting the current state of the user selection.
  // Should be used to show or hide user selection subject.
  private userSelectionSubject = new BehaviorSubject(true);
  userSelectionObservable = this.userSelectionSubject.asObservable();

  constructor(
    private readonly apiService: CpMarketplaceApiService,  
  ) { }

  setLoginState(loginState: Boolean) {
    this.loginSubject.next(loginState);
    if (this.userData) {
      this.userData.isLoggedIn = loginState;
    }
  }

  setboardResolution(boardResolutionId) {
    this.userData.boardResolutionId = boardResolutionId;
  }

  getboardResolution() {
    return this.userData.boardResolutionId;
  }
   /**
   * Gets the public key from the backend. Uses the retreived public key to encrypt.
   */
   getEncrytedPublicKey(): void {
    this.apiService.getPublicKey().subscribe((resp: any) => {
      if(resp) {
        this.encryptionKey = resp.credentials_public_key;     
      }
    });
  }
  parseUserData(userData) {
    const parsedUserData = { ...userData };
    if (userData.party_profile) {
      parsedUserData.companyName = userData.user_company?.company_name || "";
      parsedUserData.username = userData.auth_details?.name || "";
      parsedUserData.corporateId = userData.party_profile?.company.id || "";
      parsedUserData.partyProfileRoles = this.parsePartyProfileRoles(
        userData.party_profile
      );
    }
    if (userData.corporate_profile) {
      parsedUserData.corporateProfileRoles = this.parseCorporateProfileRoles(
        userData.corporate_profile
      );
    }

    return parsedUserData;
  }

  parsePartyProfileRoles(partyProfile) {
    const partyProfileRoles = [];

    partyProfile.company.divisions.forEach((division) => {
      division.instrument.forEach((instrument) => {
        partyProfileRoles.push(...instrument.roles);
      });
    });

    return partyProfileRoles;
  }

  parseCorporateProfileRoles(corporateProfile) {
    const corporateProfileRoles = [];

    corporateProfile.CompanyDivision.forEach((division) => {
      division.AssociatedInstrument.forEach((instrument) => {
        corporateProfileRoles.push(...instrument.AssociatedRole);
      });
    });

    return corporateProfileRoles;
  }
  // Encrypt data
  private encryptData(data: any): string {
    return CryptoJS.AES.encrypt(
      JSON.stringify(data),
      this.encryptionKey
    ).toString();
  }
  // Decrypt data
  private decryptData(encryptedData: string): any {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (error) {
      console.error("Decryption failed:", error);
      return null;
    }
  }
  storeUserDataInLS(userData) {
    this.userData = this.parseUserData(userData);
    const encryptedData = this.encryptData(this.userData);
    sessionStorage.setItem("userData", encryptedData);
  }
  getUserDataFromLS() {
    const encryptedData = sessionStorage.getItem("userData");
    return encryptedData ? this.decryptData(encryptedData) : null;
  }
  /**
   * Stores the user selection data in session storage.
   * @param userSelectionData - User's selection object which contains selected division, instrument and role.
   */
  storeUserSelection(userSelectionData: UserSelectionData) {
    this.setUserSelectionDetailsToUserData(userSelectionData);
    const encryptedData = this.encryptData(userSelectionData);
    sessionStorage.setItem("userRoleSelection", encryptedData);
  }

  /**
   * Sets the User Role Selection Data to the user data in the sessionstorage.
   * @param userSelectionData - User Role Selection Data.
   */
  setUserSelectionDetailsToUserData(userSelectionData: UserSelectionData) {
    const newUserData = Object.assign({}, this.getUserDataFromLS(), {
      isCorporateAdmin: userSelectionData.iscadmin,
      divisionId: userSelectionData.divisionId,
      divisionName: userSelectionData.divisionName,
      instrumentId: userSelectionData.instrumentId,
      instrumentType: userSelectionData.instrumentType,
      roleId: userSelectionData.roleId,
      roleType: userSelectionData.roleType,
      hasMultipleRoles: userSelectionData.hasMultipleRoles,
    });

    this.storeUserDataInLS(newUserData);
  }

  /**
   * Gets the user selection details from session storage and returns it.
   */
  getUserSelectionFromLS(): UserSelectionData {
    const encryptedData = sessionStorage.getItem("userRoleSelection")
    return encryptedData ? this.decryptData(encryptedData) : null;
  }

  setUserSelectionState(userSelectionState) {
    this.userSelectionSubject.next(userSelectionState);
  }

  setUserDataFromLS() {
    this.userData = this.getUserDataFromLS();
  }

  /**
   * Sets the currentuser Session Storage data.
   * @param token - JWT Token.
   * @param username - Name of the User.
   */
  setCurrentUserToken(token, username) {
    const encryptedToken = this.encryptData({ username, token });
    sessionStorage.setItem("currentUser", encryptedToken);
  }

  /**
   * Returns the currentUser session storage data.
   */
  getCurrentUserToken() {
    const encryptedToken =  sessionStorage.getItem("currentUser");
    return encryptedToken ? this.decryptData(encryptedToken) : null;
  }

  updateTncInLS(tnc) {
    const _userData = this.getUserDataFromLS();
    if(_userData){
      _userData.tnc_signed = tnc;
      this.storeUserDataInLS(_userData);
    }
  }

  setHolidaysToLS(holidays) {
    const holidaysList =
      holidays?.length
        ? holidays.map((holiday) => {
            const holidayParts = holiday.holiday_date.split("-");
            return {
              year: parseInt(holidayParts[0], 10),
              month: parseInt(holidayParts[1], 10) - 1,
              day: parseInt(holidayParts[2], 10),
            };
          })
        : [];

    sessionStorage.setItem("holidaysList", JSON.stringify(holidaysList));
    setHolidays(holidaysList);
  }

  getHolidaysFromLS() {
    return JSON.parse(sessionStorage.getItem("holidaysList"));
  }

  updateHolidays() {
    setHolidays(this.getHolidaysFromLS());
  }

  setCorporateSignUpData(signUpData) {
    this.corporateSignUpData = signUpData;
  }

  getCorporateSignUpData() {
    return this.corporateSignUpData;
  }

  removeCorporateSignUpData() {
    this.corporateSignUpData = null;
  }

  /**
   * Clears the session storage.
   */
  clearSessionStorage() {
    sessionStorage.clear();
  }
}
